<script lang="ts">

    import Modal from "@/components/Modal.svelte";
    import {Button} from "@/components/ui/button";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import FormContainer from "@/components/Form/FormContainer.svelte";
    import FormBlock from "@/components/Form/FormBlock.svelte";
    import type Blocks from "@/schemas/public/Blocks";
    import type { Field, Lead } from '@/types/types';
    import {useForm, router} from "@inertiajs/svelte";
    import {toast} from "svelte-sonner";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import DateOnly from '@/components/Inputs/Date.svelte';
    import AddressLookup from '@/components/Inputs/AddressLookup.svelte';
    import RelatedSingle from '@/components/Inputs/RelatedSingle.svelte';
    import CustomerTable from '@/components/customers/CustomerTable.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { transformToNestedStructure, transformToFlatStructure } from '@/lib/formTransformers';
    import { createDateDurationSync } from '@/lib/dateDurationCalculator.svelte';
    import { isWeekendDate } from '@/lib/dateUtils';

    const WEEKEND_WARN_FIELDS = new Set(['estimated_job_start', 'estimated_job_end']);


    type Props = {
        blocks: (Blocks & { fields: Field[] })[];
        show: boolean;
        lead: Lead;
    };

    let {blocks, show = $bindable(), lead}: Props = $props();

    // Flatten nested data for form initialization
    let formInner: Record<string, any> = {};
    for (const block of blocks) {
        for (const field of block.fields) {
            formInner[field.column_name] = field.value;
        }
    }

    // Flatten lead data if it has nested structures
    const flattenedLead = transformToFlatStructure(lead as any);
    formInner = { ...formInner, ...flattenedLead };

    // Ensure address fields are always initialized (for AddressLookup to work)
    const addressFields = ['address_line_one', 'address_line_two', 'city', 'county', 'postcode', 'country', 'address_id'];
    for (const field of addressFields) {
        if (!(field in formInner)) {
            formInner[field] = null;
        }
    }

    let form = $state(useForm(formInner));

    // Estimated job date ↔ duration auto-calculation
    createDateDurationSync({
        getStartDate: () => $form.estimated_job_start,
        getEndDate: () => $form.estimated_job_end,
        getDuration: () => $form.estimated_hire_duration_days,
        setEndDate: (date) => { $form.estimated_job_end = date; },
        setDuration: (duration) => { $form.estimated_hire_duration_days = duration; },
    });

    // Additional hire date ↔ duration auto-calculation
    createDateDurationSync({
        getStartDate: () => $form.additional_hire_start_date,
        getEndDate: () => $form.additional_hire_end_date,
        getDuration: () => $form.additional_hire_duration?.toString(),
        setEndDate: (date) => { $form.additional_hire_end_date = date; },
        setDuration: (duration) => { $form.additional_hire_duration = duration; },
    });

    // State for related field pickers
    let isPrimaryCustomerOpen = $state(false);
    let primaryCustomerLabel = $state<string | null>(lead.primary_customer ?? null);

    function submit(e: SubmitEvent) {
        e.preventDefault();
        $form.processing = true;

        // Transform flat form data to nested structure
        const transformedData = transformToNestedStructure($form.data());

        // Use router.put() directly to send only the transformed data
        // This avoids $form.put() merging original form data (which includes address: null)
        router.put(route('leads.update', {lead: lead.id}), transformedData, {
            onSuccess: () => {
                close();
                $form.reset();
                toast.success("Lead edited successfully!");
            },
            onError: (errors) => {
                // Set errors on form for display
                $form.errors = errors;
                for (const message of Object.values(errors)) {
                    toast.error("Error editing lead. " + message as string);
                }
            },
            onFinish: () => {
                $form.processing = false;
            }
        });
    }

    function close() {
        show = false;
    }

    function handleAddressSelect(address: {
        line_1: string;
        line_2: string;
        city: string;
        postcode: string;
        county: string;
    }) {
        $form.address_line_one = address.line_1;
        //$form.address_line_two = address.line_2;
        $form.city = address.city;
        $form.postcode = address.postcode;
        $form.county = address.county;
    }

</script>

{#if show}
    <Modal title="Edit Lead" onClose={close}>
        <FormContainer>
            <form onsubmit={submit}>
                {#each blocks as block (block.id)}
                    <FormBlock label={block.label}>
                        {#if block.label === 'Address'}
                            <AddressLookup onAddressSelect={handleAddressSelect} />
                        {/if}
                        {#each block.fields as field (field.id)}
                            {#if field.editable}
                                {#if field.ui_type === 'checkbox'}
                                    <Checkbox
                                        bind:checked={$form[field.column_name]}
                                        label={field.label}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'select'}
                                    <Select
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        options={parseFieldOptions(field)}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'textarea'}
                                    <Textarea
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                {:else if field.ui_type === 'date'}
                                    <DateOnly
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                    {#if WEEKEND_WARN_FIELDS.has(field.column_name) && isWeekendDate($form[field.column_name])}
                                        <p class="-mt-2 mb-2 text-sm" style="color: var(--warning);" role="alert">
                                            Heads up — this falls on a weekend. You can still save it.
                                        </p>
                                    {/if}
                                {:else if field.ui_type === 'datetime'}
                                    <DateTime
                                        label={field.label}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                    />
                                    {#if WEEKEND_WARN_FIELDS.has(field.column_name) && isWeekendDate($form[field.column_name])}
                                        <p class="-mt-2 mb-2 text-sm" style="color: var(--warning);" role="alert">
                                            Heads up — this falls on a weekend. You can still save it.
                                        </p>
                                    {/if}
                                {:else if field.ui_type === 'related' && field.column_name === 'is_primary'}
                                    <RelatedSingle
                                        label={field.label}
                                        bind:value={$form.is_primary}
                                        error={$form.errors.is_primary}
                                        bind:isOpen={isPrimaryCustomerOpen}
                                        bind:valLabel={primaryCustomerLabel}
                                    >
                                        <CustomerTable
                                            view="related"
                                            onRowClick={(row) => {
                                                $form.is_primary = row?.id ?? null;
                                                primaryCustomerLabel = row?.name ?? '';
                                                isPrimaryCustomerOpen = false;

                                                // Auto-populate fields from the selected customer
                                                if (row) {
                                                    $form.first_name = row.first_name ?? '';
                                                    $form.last_name = row.last_name ?? '';
                                                    $form.email = row.email ?? '';
                                                    $form.phone = row.phone ?? '';
                                                    $form.organisation_name = row.organisation_name ?? '';
                                                }
                                            }}
                                        />
                                    </RelatedSingle>
                                {:else if field.ui_type !== 'related'}
                                    <Input
                                        field={field}
                                        bind:value={$form[field.column_name]}
                                        error={$form.errors[field.column_name]}
                                        onkeydown={() => {}}
                                    />
                                {/if}
                            {/if}
                        {/each}
                    </FormBlock>
                {/each}

                <div class="mt-5 flex justify-end gap-2">
                    <Button type="button" onclick={close}>Cancel</Button>
                    <Button type="submit" disabled={$form.processing}>
                        {#if $form.processing}Saving...{:else}Save{/if}
                    </Button>
                </div>
            </form>
        </FormContainer>
    </Modal>
{/if}
